import React, { useState, useEffect } from "react";
import { withAuth } from "../api/auth.ts";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from "@mui/icons-material";
import { TransitionProps } from "@mui/material/transitions";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { PaletteColor, PaletteColorHex, PaletteColorDarkHex } from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const OFFICE_VIEWER_URL = "https://view.officeapps.live.com/op/view.aspx?src=";

export const FilePreviewer = ({ file, open, onClose, honbuId, roomId, palette }: any) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDarkMode = useDarkMode();
  const [showControls, setShowControls] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
  }, [file?.fileId]);

  if (!file) return null;

  const protocol = window.location.protocol;
  const host = window.location.host;
  const authParams = `honbuId=${honbuId ?? 0}&roomId=${roomId ?? 0}`;
  const downloadUrl = withAuth(`${protocol}//${host}/api/files/download/${file.fileId}?${authParams}`);
  const previewUrl = withAuth(`${protocol}//${host}/api/files/preview/${file.fileId}?${authParams}`);

  const isPdf = file.fileName.toLowerCase().endsWith(".pdf");
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.fileName);
  const isVideo = /\.(mp4|webm|ogg|mov|m4v|3gp)$/i.test(file.fileName);
  const isOffice = /\.(docx|xlsx|pptx|doc|xls|ppt)$/i.test(file.fileName);

  let iframeUrl = "";
  if (isPdf) {
    iframeUrl = previewUrl;
  } else if (isOffice) {
    iframeUrl = OFFICE_VIEWER_URL + encodeURIComponent(downloadUrl);
  }

  const paletteColor: PaletteColor = palette ?? "Blue";
  const mainHex = isDarkMode ? PaletteColorDarkHex[paletteColor] : PaletteColorHex[paletteColor];
  const headerGradient = `linear-gradient(to right, ${mainHex}cc, ${mainHex})`;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar
        sx={{
          position: "relative",
          background: headerGradient,
          borderRadius: 0,
          boxShadow: "none",
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{ ml: 2, flex: 1, color: "white" }}
            variant="body2"
            noWrap
          >
            {file.fileName}
          </Typography>
          {isMobile ? (
            <IconButton
              color="inherit"
              component="a"
              href={downloadUrl}
              target="_blank"
              aria-label="download"
            >
              <DownloadIcon />
            </IconButton>
          ) : (
            <Button
              sx={{ color: "white" }}
              startIcon={<DownloadIcon />}
              href={downloadUrl}
              target="_blank"
            >
              Download
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          height: "calc(100vh - 64px)",
          bgcolor: "#525659",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {isImage ? (
          <TransformWrapper initialScale={1} minScale={0.2} maxScale={8} centerOnInit>
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                {!imageLoaded && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress sx={{ color: "rgba(255,255,255,0.7)" }} />
                  </Box>
                )}
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%" }}
                >
                  <img
                    src={previewUrl}
                    alt={file.fileName}
                    onLoad={() => setImageLoaded(true)}
                    style={{
                      maxWidth: "100vw",
                      maxHeight: "calc(100vh - 64px)",
                      objectFit: "contain",
                      opacity: imageLoaded ? 1 : 0,
                      transition: "opacity 0.2s ease",
                    }}
                  />
                </TransformComponent>

                {showControls ? (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 50,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      backgroundColor: "rgba(0,0,0,0.55)",
                      borderRadius: "24px",
                      padding: "4px 8px",
                    }}
                  >
                    <IconButton size="small" onClick={() => zoomOut()} title="縮小" sx={{ color: "white" }}>
                      <ZoomOutIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => resetTransform()} title="元の大きさに戻す" sx={{ color: "white" }}>
                      <ZoomOutMapIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => zoomIn()} title="拡大" sx={{ color: "white" }}>
                      <ZoomInIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setShowControls(false)}
                      title="ボタンを非表示"
                      sx={{ color: "rgba(255,255,255,0.6)", ml: 0.5 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 16,
                      right: 16,
                      zIndex: 50,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setShowControls(true)}
                      title="ズームボタンを表示"
                      sx={{
                        color: "white",
                        backgroundColor: "rgba(0,0,0,0.45)",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" },
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </>
            )}
          </TransformWrapper>
        ) : isVideo ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <video
              key={file.fileId}
              controls
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                outline: "none",
              }}
            >
              <source src={previewUrl} type={file.contentType ?? "video/mp4"} />
              お使いのブラウザは動画の再生に対応していません。
            </video>
          </Box>
        ) : iframeUrl ? (
          <iframe
            src={iframeUrl}
            width="100%"
            height="100%"
            style={{ border: "none", backgroundColor: "white" }}
            title="Preview"
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: "white",
              flexDirection: "column",
              mt: 10,
            }}
          >
            <Typography variant="h6">
              プレビュー非対応のファイル形式です
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              href={downloadUrl}
              sx={{ mt: 2 }}
            >
              ダウンロードして確認
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};
